// Analytics-report chart renderers — PURE PRESENTATIONAL.
//
// These take their data via props and know nothing about the store, the data
// hooks, or the mock/preview datasets. The container (`../charts-row.tsx`) owns
// all data/store wiring and composes these renderers; keeping them props-only
// makes them design-system-ready.
//
// DEV-2432 rules every renderer follows:
//   • Categories are ranked by the metric, descending; every variable is capped
//     at ten and the grey "Other (N)" appears only where the ticket allows it
//     (donut slice; stacked-bar segment). Bars drop their tail.
//   • Axis ticks, bar values, legend values and tooltips print in the METRIC's
//     own unit ($ / % / count) through one `ChartFormat` per panel.
//   • Every value carries the mark the dimension table shows beside it (flag /
//     platform logo / per-value glyph) in the legend, the x-axis and the tooltip.
//   • The legend is the DS ChartLegend: three rows, then a framed scroll box.
//   • Hovering a bar, an arc, a segment or a legend row dims everything else.
//
// Bar + stacked hover tooltips are the DS ChartTooltip (dark surface on light
// mode, light on dark), pinned to the cursor with no arrow. The donut drives the
// same portaled card off its arcs.
import {
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
	type MouseEvent as ReactMouseEvent,
	type ReactNode
} from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import {
	ChartLegend,
	ChartLegendRow,
	ChartTooltip,
	ChartTooltipList,
	ChartTooltipRow,
	ChartTooltipSwatch,
	ChartTooltipTitle,
	Gauge,
	type GaugeSegment
} from '@clickguard/design-system'
import { CHART_MAX_CATEGORIES } from 'api-models'
import { usePrefersReducedMotion } from '../customize-reveal'
import { CatMark } from '../source-icon'
import type { ChartCat, ChartStack, ChartStackCol, ChartStackSeg } from '../../data/chart-adapters'
import type { ChartFormat } from '../../data/format-metric'

// The chart shapes are owned by the adapters; re-exported under the names the
// existing importers use.
export type Cat = ChartCat
export type StackData = ChartStack
export type { ChartFormat }

// ── Y-axis scale ────────────────────────────────────────────────────────────
// The axis top is the tallest bar plus a tenth, rounded UP to the nearest round
// gridline. The tenth is breathing room: a bar that ends exactly on the top
// gridline reads as clipped, and a stacked column's top segment loses its
// rounded cap against the line.
const AXIS_HEADROOM = 1.1
// Gridline intervals that read as round numbers, in order of how round they
// are. The list is walked in THIS order and a later entry must beat the running
// best outright, so when two combinations reach the same top the rounder step
// wins: 0 / 10 / 20 / 30 / 40 rather than 0 / 8 / 16 / 24 / 32 / 40.
const NICE_STEPS = [1, 2, 5, 10, 2.5, 1.5, 3, 4, 6, 8, 1.2]
// How many gaps the axis may draw. Five is the first preference; four is
// allowed because it often lands much closer to the data.
const TICK_COUNTS = [5, 4]

/** The axis top, and the number of gaps that divide it. Picks the SMALLEST
 *  round top that still clears the data plus its headroom. `cap` bounds the
 *  result for an axis that cannot exceed a value (a share axis caps at 100).
 *  `minStep` is the finest gap the tick labels can print (1 for whole-unit
 *  counts / money, 0.1 for rates): the axis never steps below it, so tiny or
 *  all-zero data gets 0…5 rather than six ticks that all round to the same
 *  label. Gaps must also be whole multiples of it, so a $1.5 gap can't be
 *  printed as $2 · $3 · $5. */
export const niceAxis = (
	raw: number,
	cap?: number,
	minStep = 0
): { top: number; steps: number } => {
	if (!(raw > 0)) return { top: cap ?? (minStep > 0 ? 5 * minStep : 1), steps: 5 }
	const target = raw * AXIS_HEADROOM
	const decade = Math.max(Math.pow(10, Math.floor(Math.log10(target / 5))), minStep)
	let best: { top: number; steps: number } | null = null
	for (const scale of [decade, decade * 10]) {
		for (const mult of NICE_STEPS) {
			const gap = mult * scale
			if (minStep > 0 && Math.abs(gap / minStep - Math.round(gap / minStep)) > 1e-9) continue
			for (const steps of TICK_COUNTS) {
				// `0.1 * 3 * 4` is 1.2000000000000002 in binary floating point, and that
				// value goes straight into a tick label. 12 digits clears the noise.
				const top = +(gap * steps).toPrecision(12)
				if (top < target) continue
				if (cap !== undefined && top > cap) continue
				if (!best || top < best.top) best = { top, steps }
			}
		}
	}
	// Nothing round fits under the cap, so the cap itself is the axis.
	return best ?? { top: cap ?? Math.max(target, 1), steps: 5 }
}

// Evenly-spaced tick values from `max` down to 0 (top-first).
export const axisTicks = (max: number, steps = 5) =>
	Array.from({ length: steps + 1 }, (_, i) => max - (max / steps) * i)

// The gap between a tick label and the gridline it labels, and the tick type.
const AXIS_TICK_PX = 11
const AXIS_GAP = 8

// Width of the widest tick label, in px. A fixed gutter fits a percent axis
// ("100%") but not a money axis ("$50,000"), which painted under the first
// column. A canvas measure is exact and synchronous — a DOM measure would need a
// layout pass, so the plot would paint once at the wrong offset and then jump.
// Measured once per distinct label set: the labels only change with the axis
// top or the format, not with every hover-driven re-render.
let tickCtx: CanvasRenderingContext2D | null | undefined
let tickFont = ''
const labelWidths = new Map<string, number>()
const axisLabelWidth = (labels: string[]): number => {
	const key = labels.join(' ')
	const hit = labelWidths.get(key)
	if (hit !== undefined) return hit
	if (tickCtx === undefined) {
		tickCtx =
			typeof document === 'undefined' ? null : document.createElement('canvas').getContext('2d')
		tickFont =
			typeof document === 'undefined'
				? ''
				: getComputedStyle(document.body).fontFamily || 'sans-serif'
	}
	// 32 is the old `w-8`, which every percent axis already fit.
	if (!tickCtx) return 32
	tickCtx.font = `${AXIS_TICK_PX}px ${tickFont}`
	const widest = Math.max(...labels.map((l) => tickCtx!.measureText(l).width))
	// A label that does NOT fit spills to the RIGHT, over the plot; +3 buys the
	// slack for a measure taken while the web font is still on its way.
	const width = Math.max(32, Math.ceil(widest) + 3)
	labelWidths.set(key, width)
	return width
}

// The tick labels and the gutter they need. Both call sites read `plotLeft` and
// hand `labelW` straight back to `AxisGrid`, so the labels and the plot can
// never disagree about where the plot starts.
const axisGutter = (max: number, steps: number, fmtTick: (v: number) => string) => {
	const labelW = axisLabelWidth(axisTicks(max, steps).map(fmtTick))
	return { labelW, plotLeft: labelW + AXIS_GAP }
}

// Left Y-axis: absolutely-positioned gridlines with value labels, drawn behind
// the plot so you can read each bar/column off the scale. `max` is the top tick;
// the plot area starts at `labelW + AXIS_GAP` to clear the labels.
export const AxisGrid = ({
	max,
	steps = 5,
	fmtTick,
	labelW = 32
}: {
	max: number
	steps?: number
	fmtTick: (v: number) => string
	labelW?: number
}) => (
	<>
		{axisTicks(max, steps).map((v, i) => (
			<div
				key={i}
				className="absolute inset-x-0 flex -translate-y-1/2 items-center"
				style={{ top: `${100 - (v / max) * 100}%` }}
			>
				{/* gray-500, not 400: axis values are data-bearing text; 400 is ~2.5:1 on white. */}
				<span
					className="shrink-0 text-right text-[11px] tabular-nums text-gray-500"
					style={{ width: labelW }}
				>
					{fmtTick(v)}
				</span>
				{/* The fallback keeps the gridlines visible on the public render route,
				    which does not load the report page's stylesheet. */}
				<div className="h-px flex-1 bg-[var(--pa-grid,#f0f0f0)]" style={{ marginLeft: AXIS_GAP }} />
			</div>
		))}
	</>
)

// ── Cursor-following floating tooltip ───────────────────────────────────────
// A single DS ChartTooltip portaled to <body>, pinned to the pointer (offset so
// it never sits under the cursor), no arrow. Shared by every chart type.
export type TipMark = { dimensionId?: string; valueId?: string; meta?: string | null }
export type TipContent = {
	color?: string
	label: ReactNode
	/** The category's flag / brand mark / glyph, beside the title. */
	mark?: TipMark
	description?: ReactNode
}
export type TipState = TipContent & { x: number; y: number }
export type TipHandlers = {
	onTip: (e: { clientX: number; clientY: number }, content: TipContent) => void
	offTip: () => void
}

export const FloatingTip = ({ tip }: { tip: TipState | null }) => {
	const reduced = usePrefersReducedMotion()
	// Keep the last-known content/position so the card can play its EXIT while
	// `tip` is already null — otherwise AnimatePresence would have nothing to
	// render on the way out.
	const lastRef = useRef<TipState | null>(tip)
	if (tip) lastRef.current = tip
	const shown = tip ?? lastRef.current
	// Clamp the cursor-anchored x so the centered card never clips past a
	// viewport edge. Width is measured after render; until known, no clamp.
	const wrapRef = useRef<HTMLDivElement>(null)
	const [halfW, setHalfW] = useState(0)
	useLayoutEffect(() => {
		if (tip && wrapRef.current) setHalfW(wrapRef.current.offsetWidth / 2)
	})
	const clampX = (x: number) =>
		halfW ? Math.min(Math.max(x, 8 + halfW), window.innerWidth - 8 - halfW) : x
	return createPortal(
		<AnimatePresence>
			{tip && shown && (
				// The wrapper carries the cursor-following position (left/top updated on
				// every mousemove) — that MUST stay instant, so it's a plain div, never
				// animated. Only the inner card fades/scales on appear + disappear.
				// `w-max` is load-bearing: a `fixed` box with a `left` and no width takes
				// the space to the viewport's right edge as its available width, so the
				// card got narrower the further right the pointer went and rewrapped on
				// every move. `width: max-content` sizes it from its own content.
				<div
					ref={wrapRef}
					className="pointer-events-none fixed z-[1000] w-max max-w-[min(320px,calc(100vw-16px))]"
					style={{
						left: clampX(shown.x),
						top: shown.y,
						transform: 'translate(-50%, calc(-100% - 12px))'
					}}
				>
					<motion.div
						initial={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.96 }}
						animate={reduced ? { opacity: 1 } : { opacity: 1, scale: 1 }}
						exit={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.96 }}
						transition={{ duration: 0.12, ease: [0.16, 1, 0.3, 1] }}
						style={{ transformOrigin: 'center bottom' }}
					>
						{/* The title gets a colour swatch ONLY when a `color` is passed — a
						    bar's category is itself a colour, but a stacked column's title is
						    a plain axis label; its segment colours live in the rows below. */}
						<ChartTooltip>
							<ChartTooltipTitle
								swatch={shown.color}
								lead={
									shown.mark ? (
										<CatMark
											dimensionId={shown.mark.dimensionId}
											valueId={shown.mark.valueId}
											iconMeta={shown.mark.meta}
											className="h-4 w-4 shrink-0"
										/>
									) : undefined
								}
							>
								<span className="truncate">{shown.label}</span>
							</ChartTooltipTitle>
							{shown.description != null && shown.description}
						</ChartTooltip>
					</motion.div>
				</div>
			)}
		</AnimatePresence>,
		document.body
	)
}

// ── Shared bits ─────────────────────────────────────────────────────────────
const isOtherCat = (c: ChartCat) => c.key === '__other__'

const markOf = (c: { dimensionId?: string; key: string; meta?: string | null }): TipMark | undefined =>
	c.key === '__other__' ? undefined : { dimensionId: c.dimensionId, valueId: c.key, meta: c.meta }

// Print-only "Other" note (PDF capture mode). The grey slice's hover tip does
// not exist on paper, so while a panel carries data-pdf-capture (set by
// capture-charts.ts around the snapshot) this line says what the bucket holds.
// Hidden in the app; see customize.css.
const OtherFootnote = ({ cats }: { cats: ChartCat[] }) => {
	const other = cats.find(isOtherCat)
	if (!other || !other.otherCount) return null
	return (
		<div className="pa-pdf-only truncate text-xs text-[rgb(var(--cg-text-tertiary))]">
			{other.label} holds the {other.otherCount} categories past the top {CHART_MAX_CATEGORIES}.
		</div>
	)
}

/** Cross-highlight wiring, shared by a panel's chart and its legend. `hi` is the
 *  category key that holds the panel's highlight right now, and it can be set
 *  from EITHER side: hover a legend row and the matching bar or arc stays lit,
 *  hover the chart and the legend row lights up. Everything else drops back. */
type HiProps = { hi: string | null; onHi: (key: string | null) => void }

// ── Legend ──────────────────────────────────────────────────────────────────
/** One legend row, whatever the chart type: a category or a stack key. */
interface LegendItem {
	key: string
	label: string
	color: string
	dimensionId?: string
	meta?: string | null
	isOther?: boolean
	value?: number
	pct?: number
}

/** The legend every chart type shares: the DS ChartLegend (three rows, then a
 *  framed scroll box), one row per item with its mark and the value the chart
 *  type prints beside it (§2.2) — a donut the number and the share, a bar the
 *  metric value alone, a stacked bar nothing. */
const Legend = ({
	items,
	valueText,
	hi,
	onHi
}: {
	items: LegendItem[]
	valueText?: (item: LegendItem) => string
} & HiProps) => (
	<div className="pa-legend w-full">
		<ChartLegend visibleRows={3} framed="auto">
			{items.map((it) => (
				<ChartLegendRow
					key={it.key}
					color={it.color}
					label={it.label}
					leading={
						it.isOther ? undefined : (
							<CatMark dimensionId={it.dimensionId} valueId={it.key} iconMeta={it.meta} className="h-4 w-4" />
						)
					}
					value={valueText?.(it)}
					dim={hi !== null && hi !== it.key}
					onMouseEnter={() => onHi(it.key)}
					onMouseLeave={() => onHi(null)}
				/>
			))}
		</ChartLegend>
	</div>
)

const catItems = (cats: ChartCat[]): LegendItem[] =>
	cats.map((c) => ({ ...c, isOther: isOtherCat(c) }))

// ── Plot sizing ─────────────────────────────────────────────────────────────
// One plot height for every chart type. With the plot fixed and the legend
// fixed (the DS ChartLegend holds one height whatever it lists), all three
// panels in a row hold the same height on their own.
const PLOT_H = 190

// ── Chart-type renderers (bespoke DS look) ──────────────────────────────────

// Bar — one bar per category, ranked, top ten only. Bars plot the metric VALUE
// (never a share): the axis follows the metric's unit and scales to the data
// (§5.3), capped at 100 for a rate metric, whose values are already shares.
const BarViz = ({
	cats,
	metric,
	format,
	onTip,
	offTip,
	hi,
	onHi
}: { cats: ChartCat[]; metric: string; format: ChartFormat } & TipHandlers & HiProps) => {
	const maxV = Math.max(...cats.map((c) => c.value), 0)
	const { top, steps } = niceAxis(maxV, format.isPct ? 100 : undefined, format.tickStep)
	const { labelW, plotLeft } = axisGutter(top, steps, format.fmtTick)
	return (
		<div className="relative" style={{ height: PLOT_H }}>
			<AxisGrid max={top} steps={steps} fmtTick={format.fmtTick} labelW={labelW} />
			{/* The gap closes up past five bars: a panel can draw ten, and a fixed
			    16px gap ate more of the plot than the bars did. */}
			<div
				className={`absolute inset-y-0 right-0 flex items-stretch justify-around ${
					cats.length > 5 ? 'gap-2' : 'gap-4'
				}`}
				style={{ left: plotLeft }}
			>
				{cats.map((b) => (
					<div
						key={b.key}
						className="flex h-full min-w-0 max-w-[64px] flex-1 flex-col justify-end rounded-t-md outline-none"
						onMouseMove={(e) =>
							onTip(e, {
								color: b.color,
								label: b.label,
								mark: markOf(b),
								// A plain bar's tooltip is the metric's value in its own unit —
								// the category is already the title.
								description: (
									<ChartTooltipList>
										<ChartTooltipRow label={metric} value={format.fmtValue(b.value)} />
									</ChartTooltipList>
								)
							})
						}
						onMouseEnter={() => onHi(b.key)}
						onMouseLeave={() => {
							offTip()
							onHi(null)
						}}
					>
						{/* Print-only value label (PDF capture mode) — on screen this
						    number lives in the hover tooltip, which paper doesn't have. */}
						<span className="pa-pdf-value tabular-nums">{format.fmtValue(b.value)}</span>
						{/* The dim is on the BAR, not on the column: the column also
						    holds the print-only value, and that must not fade on paper. */}
						<div
							className="pa-bar-grow rounded-t-md transition-opacity duration-150 motion-reduce:transition-none"
							style={{
								height: `${top ? (b.value / top) * 100 : 0}%`,
								// Every bar draws at least a 2px line — a real but tiny category
								// must not vanish (the reader would count one bar fewer than the
								// legend lists), and a zero bar still marks its category so an
								// empty chart reads as "these, with nothing in them", not a
								// blank plot (Cherry #0004).
								minHeight: 2,
								backgroundColor: b.color,
								opacity: hi !== null && hi !== b.key ? 0.25 : 1
							}}
						/>
					</div>
				))}
			</div>
		</div>
	)
}

// Pie — DS Gauge donut, one segment per category (+ the grey Other), with the
// running total centred in the metric's unit.
const DonutViz = ({
	cats,
	metric,
	format,
	onTip,
	offTip,
	hi,
	onHi
}: { cats: ChartCat[]; metric: string; format: ChartFormat } & TipHandlers & HiProps) => {
	const total = cats.reduce((s, c) => s + c.value, 0)
	const hiIndex = hi === null ? -1 : cats.findIndex((c) => c.key === hi)
	const wrapRef = useRef<HTMLDivElement>(null)
	// Dim every arc but the highlighted one. The DS Gauge draws the paths, so
	// there is no element of ours to put a class on; this reaches them by
	// position exactly as the Gauge writes them.
	useEffect(() => {
		const paths = wrapRef.current?.querySelectorAll<SVGPathElement>('svg path')
		if (!paths) return
		paths.forEach((path, i) => {
			path.style.opacity = hiIndex < 0 ? '' : i === hiIndex ? '1' : '0.25'
		})
	}, [hiIndex, cats])
	// The DS Gauge's built-in tooltip renders INLINE (no portal), so it's clipped
	// by the chart card + ReportSection overflow-hidden. Drive our own portaled
	// FloatingTip instead by delegating hover off the segment <path>s. The Gauge
	// renders one path per segment in `segments` order, so path index maps to
	// `cats` index; bail if the counts ever diverge rather than mis-label.
	const onMove = (e: ReactMouseEvent) => {
		const svg = wrapRef.current?.querySelector('svg')
		const path = (e.target as Element).closest?.('path')
		const paths = svg ? [...svg.querySelectorAll('path')] : []
		const c = path && paths.length === cats.length ? cats[paths.indexOf(path)] : undefined
		if (!c) {
			offTip()
			onHi(null)
			return
		}
		onHi(c.key)
		onTip(e, {
			color: c.color,
			label: c.label,
			mark: markOf(c),
			description: (
				<ChartTooltipList>
					<ChartTooltipRow label={metric} value={`${format.fmtValue(c.value)} · ${c.pct}%`} />
				</ChartTooltipList>
			)
		})
	}
	return (
		<div
			ref={wrapRef}
			className="pa-donut-hover flex items-center justify-center"
			style={{ height: PLOT_H }}
			onMouseMove={onMove}
			onMouseLeave={() => {
				offTip()
				onHi(null)
			}}
		>
			<Gauge
				shape="full"
				size="md"
				max={total}
				track={false}
				segments={cats.map<GaugeSegment>((v) => ({
					value: v.value,
					color: v.color,
					label: v.label
				}))}
			>
				<div className="w-[160px] text-center">
					<div className="truncate text-[28px] font-semibold leading-tight tabular-nums text-gray-900">
						{format.fmtValue(total)}
					</div>
					<div className="truncate text-sm text-gray-500">{metric}</div>
				</div>
			</Gauge>
		</div>
	)
}

// Stacked bar — one column per top-ten primary category, each split into the
// global top-ten stack categories (same order and colour in every column) plus
// that column's own grey "Other (N)" on top. `stretch` (the 100% toggle)
// normalizes every column to fill the plot; without it columns scale to the
// tallest total in the metric's unit.
const StackedViz = ({
	data,
	stretch,
	format,
	onTip,
	offTip,
	hi,
	onHi
}: { data: ChartStack; stretch: boolean; format: ChartFormat } & TipHandlers & HiProps) => {
	const { cols, dimensionId, dimension2Id } = data
	const maxTotal = Math.max(...cols.map((c) => c.total), 0)
	// 100%-stretched columns all fill the plot, so the axis is a fixed 0-100%.
	// Otherwise column height tracks the raw total, so scale to a nice ceiling
	// and label the ticks in the metric's unit.
	const { top, steps } = stretch ? { top: 100, steps: 5 } : niceAxis(maxTotal, undefined, format.tickStep)
	const fmtTick = stretch ? (v: number) => `${Math.round(v)}%` : format.fmtTick
	// A money axis prints "$50,000", so the gutter is measured, not fixed.
	const { labelW, plotLeft } = axisGutter(top, steps, fmtTick)
	// The TOOLTIP is ranked (§4): biggest first, the rolled-up "Other (N)" LAST
	// whatever its value, then a divider and the Total (100%). Only a stackable
	// (additive) metric reaches here, so the Total is always meaningful.
	const tooltip = (col: ChartStackCol) => {
		const p = (n: number) => (col.total ? Math.round((n / col.total) * 100) : 0)
		const ranked = [...col.segs]
			.filter((s) => s.value > 0)
			.sort((a, b) => (!!a.isOther !== !!b.isOther ? (a.isOther ? 1 : -1) : b.value - a.value))
		return (
			<ChartTooltipList>
				{ranked.map((seg: ChartStackSeg) => (
					<ChartTooltipRow
						key={seg.key}
						className="[&>span:first-child]:min-w-0 [&>span:last-child]:whitespace-nowrap"
						label={
							<span className="flex min-w-0 items-center gap-1.5">
								<ChartTooltipSwatch color={seg.color} />
								{!seg.isOther && (
									<CatMark dimensionId={dimension2Id} valueId={seg.key} iconMeta={seg.meta} className="h-3.5 w-3.5 shrink-0" />
								)}
								<span className="truncate">{seg.label}</span>
							</span>
						}
						value={`${format.fmtValue(seg.value)} (${p(seg.value)}%)`}
					/>
				))}
				<ChartTooltipRow total label="Total" value={`${format.fmtValue(col.total)} (100%)`} />
			</ChartTooltipList>
		)
	}
	return (
		<div className="flex flex-col" style={{ height: PLOT_H }}>
			{/* The x labels sit at the bottom; the plot takes the rest of PLOT_H. */}
			<div className="relative min-h-0 flex-1">
				<AxisGrid max={top} steps={steps} fmtTick={fmtTick} labelW={labelW} />
				<div
					className="absolute inset-y-0 right-0 flex items-end justify-evenly gap-2"
					style={{ left: plotLeft }}
				>
					{cols.map((col) => (
						<div
							key={col.key}
							// `rounded-t-md`, NOT `rounded-md`: the column sits ON the axis,
							// so its bottom corners must be square, like the plain bars.
							className="pa-stack-col flex min-w-0 max-w-[56px] flex-1 flex-col overflow-hidden rounded-t-md"
							style={{
								// A 100%-stacked column with NO data has no shares to paint: give
								// it no height (not a full-height blank hover target).
								height: stretch
									? col.total > 0
										? '100%'
										: '0%'
									: `${top ? Math.max(4, (col.total / top) * 100) : 0}%`
							}}
							onMouseMove={(e) =>
								onTip(e, {
									// No title swatch: the column title is a plain axis category;
									// the segment colours are shown on the description rows.
									label: col.label,
									mark: { dimensionId, valueId: col.key, meta: col.meta },
									description: tooltip(col)
								})
							}
							onMouseLeave={() => {
								offTip()
								onHi(null)
							}}
						>
							{/* Rendered top-first (DOM order = visual order in a column
							    flexbox), so the segKeys order stacks bottom-up with Other last. */}
							{[...col.segs].reverse().map((seg) => (
								<div
									key={seg.key}
									className="pa-stack-seg transition-opacity duration-150 motion-reduce:transition-none"
									// A segment key repeats in every column, so lighting one lights
									// that series across the whole plot.
									onMouseEnter={() => onHi(seg.key)}
									style={{
										flex: seg.value,
										backgroundColor: seg.color,
										opacity: hi !== null && hi !== seg.key ? 0.25 : 1
									}}
								/>
							))}
						</div>
					))}
				</div>
			</div>
			{/* X labels align under the columns, so share the same left gutter. The
			    icon stays fully visible and only the text truncates (§2.4); the
			    full value shows on hover through `title`. */}
			<div className="mt-2 flex justify-evenly gap-2" style={{ paddingLeft: plotLeft }}>
				{cols.map((col) => (
					<span
						key={col.key}
						title={col.label}
						className="flex min-w-0 max-w-[56px] flex-1 items-center justify-center gap-1 text-[11px] text-gray-500"
					>
						<CatMark dimensionId={dimensionId} valueId={col.key} iconMeta={col.meta} className="h-3.5 w-3.5" />
						<span className="truncate">{col.label}</span>
					</span>
				))}
			</div>
		</div>
	)
}

// Print-only per-column segment values for a Stacked bar chart (PDF capture
// mode). The segment values only show up on hover in the app, so while a panel
// carries data-pdf-capture this lists each column's values as plain text under
// the legend. Hidden in the app; see customize.css.
const StackPrintValues = ({ data, format }: { data: ChartStack; format: ChartFormat }) => (
	<>
		{data.cols.map((col) => (
			<div key={col.key} className="pa-pdf-only truncate text-xs text-[rgb(var(--cg-text-tertiary))]">
				{col.label}:{' '}
				{col.segs
					.filter((seg) => seg.value > 0)
					.map((seg) => `${seg.label} ${format.fmtValue(seg.value)}`)
					.join(' · ')}
			</div>
		))}
	</>
)

// The minimal chart-config shape ChartBody reads. The full ChartCfg lives in the
// store (`../store/use-report-layout-store`); this presentational module stays
// store-free, so it takes only the fields it dispatches on as a local prop type.
export type ChartBodyCfg = {
	chartType: string
	stack100: boolean
	metric: string
}

// Dispatch a panel's body on its configured chart type. Bar / Pie use the 1-D
// `cats`; Stacked bar uses the 2-D `stack`.
const ChartBody = ({
	cfg,
	cats,
	stack,
	format,
	onTip,
	offTip
}: {
	cfg: ChartBodyCfg
	cats: ChartCat[]
	stack: ChartStack
	/** How this panel prints its numbers. ONE formatter for the whole panel, so
	 *  the axis, the bars, the legend and the tooltip agree on the unit. */
	format: ChartFormat
} & TipHandlers) => {
	// The panel's cross-highlight. One key at a time holds it, and either side
	// can set it. The chart body remounts on a config change (its AnimatePresence
	// key is `chartType|dimension|dimension2`), so this resets with the data.
	const [hi, setHi] = useState<string | null>(null)
	const hiProps: HiProps = { hi, onHi: setHi }
	switch (cfg.chartType) {
		case 'Pie':
			return (
				<>
					<DonutViz cats={cats} metric={cfg.metric} format={format} onTip={onTip} offTip={offTip} {...hiProps} />
					<Legend
						items={catItems(cats)}
						valueText={(c) => `${format.fmtValue(c.value ?? 0)} · ${c.pct ?? 0}%`}
						{...hiProps}
					/>
					<OtherFootnote cats={cats} />
				</>
			)
		case 'Stacked bar':
			return (
				<>
					<StackedViz data={stack} stretch={cfg.stack100} format={format} onTip={onTip} offTip={offTip} {...hiProps} />
					<Legend items={stack.segKeys.map((k) => ({ ...k, dimensionId: stack.dimension2Id }))} {...hiProps} />
					<StackPrintValues data={stack} format={format} />
				</>
			)
		case 'Bar':
		default:
			return (
				<>
					<BarViz cats={cats} metric={cfg.metric} format={format} onTip={onTip} offTip={offTip} {...hiProps} />
					<Legend items={catItems(cats)} valueText={(c) => format.fmtValue(c.value ?? 0)} {...hiProps} />
				</>
			)
	}
}

export { OtherFootnote, Legend, BarViz, DonutViz, StackedViz, StackPrintValues, ChartBody }
